import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { faWindowMinimize } from '@fortawesome/free-solid-svg-icons';
import { isDefined } from '@sama/js-core';
import { bindKey, every } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Selection } from '../../../../../../../core/h3ml/state/fusion-state';
import { outputInfoStoreToken } from '../../../../../../../core/h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../../core/h3ml/state/task-workspace-store';
import { BulkRemoveKeyframesModalComponent } from '../../../../../../../core/outputs/annotation/annotation-shared/toolbars/shape-list/bulk-remove-keyframes-modal/bulk-remove-keyframes-modal.component';
import { BulkRemoveFrameAnnotationsService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/bulk-remove-frame-annotations.service';
import { KeyframeService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/keyframe.service';
import { ShapeSelectionService } from '../../../../../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import { RootKeyboardShortcutService } from '../../../../../../../core/shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { ShapeAccessService } from '../../../../../../../core/shared/misc-services/shape-access.service';
import { isKeyLocation } from '../../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { selectionToArray } from '../../../../../../../cross-team-shared/misc-utils';
import { outputAnnotationInfoStoreToken } from '../../../../annotation-providers';
import { AbstractAnnotationStore } from '../../../../state/root/abstract-annotation-store';

@Component({
  selector: 'h3-key-location-actions',
  templateUrl: './key-location-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KeyLocationActionsComponent extends DirectiveSuperclass {
  private readonly shapesStore = inject(outputAnnotationInfoStoreToken)(
    'shapes',
  );
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly outputInfoStore = inject(outputInfoStoreToken);

  faWindowMinimize = faWindowMinimize;
  copyLastKeyframeDisabled$!: Observable<boolean>;
  toggleKeyframeDisabled$!: Observable<boolean>;
  bulkRemoveKeyframeDisabled$!: Observable<boolean>;
  selectedShapesHaveKeyLocation$!: Observable<boolean>;
  copyLastKeyframeTooltip: string;
  toggleKeyframeTooltip: string;
  private isShapeEditable = bindKey(this.shapeAccessService, 'isShapeEditable');

  constructor(
    public store: AbstractAnnotationStore,
    protected keyboardShortcutService: RootKeyboardShortcutService,
    private bulkRemoveFrameAnnotationsService: BulkRemoveFrameAnnotationsService,
    private dialog: MatDialog,
    private keyframeService: KeyframeService,
    private shapeAccessService: ShapeAccessService,
    private selectionService: ShapeSelectionService,
    injector: Injector,
  ) {
    super(injector);
    this.toggleKeyframeTooltip =
      'Insert keyframe' +
      keyboardShortcutService.tooltipSuffix('toggleKeyframe');
    this.copyLastKeyframeTooltip =
      'Copy Last Keyframe' +
      this.keyboardShortcutService.tooltipSuffix('copyLastKeyframe');

    this.setUpSubscriptions();
  }

  toggleKeyframes(source: string) {
    this.keyframeService.toggleKeyframesForSelectedShapes(source);
  }

  copyLastKeyframe(source: string) {
    this.keyframeService.copyLastKeyframeForSelectedShapes(source);
  }

  bulkRemoveKeyframes(source: string) {
    const dialogRef = this.dialog.open(BulkRemoveKeyframesModalComponent, {
      minWidth: '320px',
      data: {
        store: this.store,
        taskWorkspaceStore: this.taskWorkspaceStore,
        outputInfoStore: this.outputInfoStore,
      },
    });
    this.subscribeTo(
      dialogRef.afterClosed().pipe(filter(isDefined)),
      (options) => {
        this.bulkRemoveFrameAnnotationsService.bulkRemoveKeyframes(
          source,
          options,
        );
      },
    );
  }

  private getSelectedShapes(selection: Selection) {
    return selectionToArray(selection)
      .map((shapeIndex) => this.shapesStore.state()[shapeIndex])
      .filter(isDefined);
  }

  private setUpSubscriptions() {
    this.subscribeToCanToggleKeyframe();
    this.subscribeToCanCopyKeyframe();
    this.subscribeToKeyLocationStatus();
    this.subscribeToBulkRemoveKeyframeDisabled();
  }

  private subscribeToCanToggleKeyframe() {
    const locationChange$ = combineLatest([
      this.selectionService.getSelectionForOutput$(),
      this.store('frameNumber').$,
      this.taskWorkspaceStore('readOnly').$,
      this.shapesStore.$,
    ]);

    this.toggleKeyframeDisabled$ = locationChange$.pipe(
      map(
        // @ts-ignore -- fix me
        ([selection, frameNumber, readOnly]) =>
          readOnly ||
          every(
            // @ts-ignore -- fix me
            this.getSelectedShapes(selection),
            ({ key_locations }) =>
              key_locations.length === 1 &&
              // @ts-ignore -- fix me
              isKeyLocation(key_locations, frameNumber),
          ) ||
          // @ts-ignore -- fix me
          !every(selectionToArray(selection), this.isShapeEditable),
      ),
    );
  }

  private subscribeToCanCopyKeyframe() {
    const locationChange$ = combineLatest([
      this.selectionService.getSelectionForOutput$(),
      this.store('frameNumber').$,
      this.taskWorkspaceStore('readOnly').$,
      this.shapesStore.$,
    ]);

    this.copyLastKeyframeDisabled$ = locationChange$.pipe(
      map(
        // @ts-ignore -- fix me
        ([selection, currentFrameNumber, readOnly]) =>
          readOnly ||
          // @ts-ignore -- fix me
          every(this.getSelectedShapes(selection), ({ key_locations }) =>
            key_locations.every(
              // @ts-ignore -- fix me
              (location) => location.frame_number >= currentFrameNumber,
            ),
          ),
      ),
    );
  }

  private subscribeToKeyLocationStatus() {
    const locationChange$ = combineLatest([
      this.selectionService.getSelectionForOutput$(),
      this.store('frameNumber').$,
      this.shapesStore.$,
    ]);

    this.selectedShapesHaveKeyLocation$ = locationChange$.pipe(
      // @ts-ignore -- fix me
      map(([selection, frameNumber]) =>
        // @ts-ignore -- fix me
        every(this.getSelectedShapes(selection), ({ key_locations }) =>
          // @ts-ignore -- fix me
          isKeyLocation(key_locations, frameNumber),
        ),
      ),
    );
  }

  private subscribeToBulkRemoveKeyframeDisabled() {
    this.bulkRemoveKeyframeDisabled$ = combineLatest([
      this.selectionService.getSelectionForOutput$(),
      this.taskWorkspaceStore('readOnly').$,
    ]).pipe(
      map(
        // @ts-ignore -- fix me
        ([selection, readOnly]) =>
          readOnly ||
          // @ts-ignore -- fix me
          Object.keys(selection).length === 0 ||
          // @ts-ignore -- fix me
          !every(selectionToArray(selection), this.isShapeEditable),
      ),
    );
  }
}
